/**
 * RoamerPassportModule.ts — Module #1: the Roamer Passport™.
 *
 * A platform module (NOT a core engine): the permanent, celebratory record of
 * the user's WorkRoamer journey. Integration rules:
 *  • Registers with Roamer Core™ as an EngineAdapter via attachToCore().
 *  • ALL inbound information arrives as Core bus events (LocationChanged,
 *    MissionCompleted, PhotoUploaded, CommunityUpdated, JourneyUpdated,
 *    RoamerScoreUpdated) — the module never imports or calls other engines.
 *    Spec events without a dedicated bus name are derived: mission streaks
 *    from MissionCompleted timestamps; CommunityJoined from CommunityUpdated
 *    payloads; AchievementUnlocked is PUBLISHED by this module (as a
 *    NotificationSent with kind 'achievementUnlocked') rather than consumed.
 *  • Event payloads are treated as untrusted — parsed defensively, never
 *    fabricated: updates only happen when the payload carries real data.
 *  • Persistence at nomad:passport:v1 with record-by-record sanitization and
 *    caps; privacy settings gate every shared view; export/delete supported.
 */

import { roamerCore, type EngineAdapter, type CoreEvent } from '@/services/core';
import { storageManager } from '@/services/storage/StorageManager';
import {
  DEFAULT_PASSPORT_PRIVACY,
  XP_AWARDS,
  levelForXp,
  STAMP_CATEGORIES,
  type PassportAchievement,
  type PassportExport,
  type PassportPrivacy,
  type PassportProfile,
  type PassportStamp,
  type PassportStats,
  type PublicPassportView,
  type RoamerMoment,
  type StampCategory,
  type TimelineEvent,
  type TimelineEventKind,
} from './models';
import { getAchievementRules } from './achievements';

const STORAGE_KEY = 'nomad:passport:v1';
const MODULE_ID = 'roamer-passport-module';
const DAY = 24 * 60 * 60 * 1000;
const MAX_TIMELINE = 500;
const MAX_SCORE_HISTORY = 200;
const MAX_MOMENTS = 500;

interface PersistedState {
  profile: PassportProfile;
  privacy: PassportPrivacy;
}

function freshProfile(now: number): PassportProfile {
  return {
    passportId: `passport-${now}`,
    userId: 'local-roamer',
    issuedAt: now,
    currentLevel: 1,
    totalXp: 0,
    countriesVisited: [],
    citiesVisited: [],
    currentBase: null,
    favoriteDestination: null,
    longestStay: null,
    totalDaysRoaming: 0,
    missionCompletionCount: 0,
    scoreHistory: [],
    timeline: [],
    stamps: [],
    achievements: [],
    moments: [],
    stays: [],
  };
}

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

export class RoamerPassportModule {
  private profile: PassportProfile;
  private privacy: PassportPrivacy = { ...DEFAULT_PASSPORT_PRIVACY };
  private unsubscribes: (() => void)[] = [];
  /** Bumped on HousingUpdated (refresh signal — Stay™ owns accommodation data). */
  private housingRefreshSignals = 0;
  private listeners = new Set<() => void>();
  private missionDays: number[] = []; // day indices for streak derivation

  constructor(now: number = Date.now()) {
    this.profile = freshProfile(now);
    this.load(now);
  }

  // ── Core integration (the ONLY communication channel) ──────────────────────

  /** EngineAdapter shape used when registering with Roamer Core™. */
  buildAdapter(): EngineAdapter {
    return {
      id: MODULE_ID,
      name: 'Roamer Passport™',
      version: '1.0.0',
      capabilities: ['passport', 'stamps', 'achievements', 'travel-timeline'],
      healthCheck: async () => true,
    };
  }

  /** Register with Core and subscribe to the bus. Idempotent-safe to detach. */
  attachToCore(core: typeof roamerCore = roamerCore, now: number = Date.now()): void {
    this.detachFromCore();
    core.registerEngine(this.buildAdapter(), now);
    const sub = (name: Parameters<typeof core.on>[0], handler: (e: CoreEvent) => void) => {
      this.unsubscribes.push(core.on(name, (e) => {
        try {
          handler(e);
        } catch {
          /* a bad payload must never break the bus */
        }
      }, 'module:passport'));
    };
    sub('LocationChanged', (e) => this.onLocationChanged(e));
    sub('MissionCompleted', (e) => this.onMissionCompleted(e));
    sub('PhotoUploaded', (e) => this.onPhotoUploaded(e));
    sub('CommunityUpdated', (e) => this.onCommunityUpdated(e));
    sub('JourneyUpdated', (e) => this.onJourneyUpdated(e));
    sub('RoamerScoreUpdated', (e) => this.onScoreUpdated(e));
    // ECOSYSTEM SYNCHRONIZATION™ (Build 2.04.0): accommodation changes
    // (HousingUpdated) are a metadata-only refresh signal — Stay™ owns stay
    // records; Passport™ never copies them, it only notes that something moved.
    sub('HousingUpdated', () => { this.housingRefreshSignals += 1; });
    this.core = core;
  }

  detachFromCore(): void {
    for (const u of this.unsubscribes) u();
    this.unsubscribes = [];
    this.core = null;
  }

  /** True once attachToCore() has been called. Read-only — no mutation. */
  /** How many accommodation refresh signals arrived (liveness diagnostics). */
  getHousingRefreshSignalCount(): number {
    return this.housingRefreshSignals;
  }

  isAttached(): boolean {
    return this.core != null;
  }

  private core: typeof roamerCore | null = null;

  // ── Event handlers (defensive parsing — never fabricate) ───────────────────

  private onLocationChanged(e: CoreEvent): void {
    const raw = (e.payload ?? {}) as Record<string, unknown>;
    // Real Core traffic wraps the location: { location: RoamerLocation }.
    // Fall back to a flat payload for forward compatibility.
    const p = (raw.location != null && typeof raw.location === 'object'
      ? raw.location
      : raw) as Record<string, unknown>;
    const city = asString(p.city) ?? asString(p.name);
    if (!city) return;
    const country = asString(p.country);
    const lat = typeof p.latitude === 'number' && Number.isFinite(p.latitude) ? p.latitude : null;
    const lon = typeof p.longitude === 'number' && Number.isFinite(p.longitude) ? p.longitude : null;
    this.recordMove(city, country, e.at, lat, lon);
  }

  private onMissionCompleted(e: CoreEvent): void {
    const p = (e.payload ?? {}) as Record<string, unknown>;
    this.profile.missionCompletionCount += 1;
    this.awardXp(XP_AWARDS.missionCompleted);
    const title = asString(p.title) ?? 'Completed a mission';
    this.addTimeline('missionCompleted', title, this.profile.currentBase?.city ?? null, e.at);
    this.earnStamp('missionCompletion', this.profile.currentBase?.city ?? 'On the road', title, e.at, 'verified');
    const dayIdx = Math.floor(e.at / DAY);
    if (!this.missionDays.includes(dayIdx)) this.missionDays.push(dayIdx);
    this.afterUpdate(e.at);
  }

  private onPhotoUploaded(e: CoreEvent): void {
    const p = (e.payload ?? {}) as Record<string, unknown>;
    const photoRef = asString(p.photoRef) ?? asString(p.url) ?? asString(p.id);
    if (!photoRef) return;
    this.addMoment(photoRef, {
      city: asString(p.city) ?? this.profile.currentBase?.city ?? null,
      country: asString(p.country) ?? this.profile.currentBase?.country ?? null,
    }, e.at);
    this.afterUpdate(e.at);
  }

  private onCommunityUpdated(e: CoreEvent): void {
    const p = (e.payload ?? {}) as Record<string, unknown>;
    // Only a joined/meetup signal counts as CommunityJoined — plain data
    // refreshes are ignored (never fabricate participation).
    const joined = p.joined === true || asString(p.kind) === 'meetup' || asString(p.action) === 'joined';
    if (!joined) return;
    const name = asString(p.name) ?? 'Community meetup';
    this.awardXp(XP_AWARDS.communityJoined);
    this.addTimeline('communityJoined', `Joined ${name}`, this.profile.currentBase?.city ?? null, e.at);
    this.earnStamp('communityMeetup', this.profile.currentBase?.city ?? 'Community', name, e.at, 'unverified');
    this.afterUpdate(e.at);
  }

  private onJourneyUpdated(e: CoreEvent): void {
    const p = (e.payload ?? {}) as Record<string, unknown>;
    const title = asString(p.title) ?? asString(p.summary);
    if (!title) return;
    this.addTimeline('journeyUpdated', title, asString(p.city), e.at);
    this.afterUpdate(e.at);
  }

  private onScoreUpdated(e: CoreEvent): void {
    const p = (e.payload ?? {}) as Record<string, unknown>;
    const score = typeof p.score === 'number' && Number.isFinite(p.score) ? p.score : null;
    if (score == null) return;
    this.profile.scoreHistory.push({ score, at: e.at });
    if (this.profile.scoreHistory.length > MAX_SCORE_HISTORY) {
      this.profile.scoreHistory = this.profile.scoreHistory.slice(-MAX_SCORE_HISTORY);
    }
    this.afterUpdate(e.at);
  }

  // ── Journey recording ───────────────────────────────────────────────────────

  private recordMove(
    city: string,
    country: string | null,
    at: number,
    latitude: number | null = null,
    longitude: number | null = null,
  ): void {
    const open = this.profile.stays.find((s) => s.departedAt == null);
    if (open && open.city === city) return; // already based here
    if (open) open.departedAt = at;
    this.profile.stays.push({ city, country, arrivedAt: at, departedAt: null, latitude, longitude });
    this.profile.currentBase = { city, country };

    const isNewCity = !this.profile.citiesVisited.includes(city);
    if (isNewCity) {
      this.profile.citiesVisited.push(city);
      // Build 2.32.0: XP + stamp only awarded for GENUINE travel — not for the
      // initial location detected at boot/onboarding. "Working from a new city"
      // means the operator has completed at least one mission first.
      // Location data (citiesVisited, currentBase, stays) is always recorded.
      if (this.profile.missionCompletionCount >= 1) {
        this.awardXp(XP_AWARDS.newCity);
        this.earnStamp('city', city, `First arrival in ${city}.`, at, 'verified');
      }
    }
    if (country && !this.profile.countriesVisited.includes(country)) {
      this.profile.countriesVisited.push(country);
      // Same guard: country XP only for active operators who have completed missions.
      if (this.profile.missionCompletionCount >= 1) {
        this.awardXp(XP_AWARDS.newCountry);
        this.earnStamp('country', country, `First entry into ${country}.`, at, 'verified');
      }
    }
    this.addTimeline(isNewCity ? 'visited' : 'movedTo', `Moved to ${city}`, city, at);
    this.afterUpdate(at);
  }

  /** Manual stamp entry point for surfaces Core has no event for yet. */
  earnStamp(
    category: StampCategory,
    location: string,
    description: string,
    at: number = Date.now(),
    verification: PassportStamp['verification'] = 'unverified',
  ): PassportStamp | null {
    if (!STAMP_CATEGORIES.includes(category)) return null;
    const stamp: PassportStamp = {
      id: `stamp-${category}-${location.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${at}`,
      category,
      location,
      earnedAt: at,
      description,
      artworkPlaceholder: `stamps/${category}.svg`,
      verification,
    };
    this.profile.stamps.push(stamp);
    this.awardXp(XP_AWARDS.stampEarned);
    this.addTimeline('stampEarned', `Earned ${category} stamp — ${location}`, location, at);
    return structuredClone(stamp);
  }

  /** Associate an uploaded photo with places/stamps/timeline (Roamer Moments). */
  addMoment(
    photoRef: string,
    assoc: { city?: string | null; country?: string | null; stampId?: string | null; timelineEventId?: string | null; collections?: string[] } = {},
    at: number = Date.now(),
  ): RoamerMoment {
    const moment: RoamerMoment = {
      id: `moment-${at}-${this.profile.moments.length}`,
      photoRef,
      city: assoc.city ?? this.profile.currentBase?.city ?? null,
      country: assoc.country ?? this.profile.currentBase?.country ?? null,
      stampId: assoc.stampId != null && this.profile.stamps.some((s) => s.id === assoc.stampId) ? assoc.stampId : null,
      timelineEventId: assoc.timelineEventId != null && this.profile.timeline.some((t) => t.id === assoc.timelineEventId) ? assoc.timelineEventId : null,
      collections: (assoc.collections ?? []).filter((c) => typeof c === 'string'),
      at,
    };
    this.profile.moments.push(moment);
    if (this.profile.moments.length > MAX_MOMENTS) {
      this.profile.moments = this.profile.moments.slice(-MAX_MOMENTS);
    }
    this.awardXp(XP_AWARDS.photoMoment);
    this.addTimeline('photoAdded', 'Added a Roamer Moment', moment.city, at);
    this.persist();
    return structuredClone(moment);
  }

  // ── XP / achievements / timeline ────────────────────────────────────────────

  private awardXp(amount: number): void {
    this.profile.totalXp += amount;
    this.profile.currentLevel = levelForXp(this.profile.totalXp);
  }

  private addTimeline(kind: TimelineEventKind, title: string, location: string | null, at: number): void {
    const event: TimelineEvent = {
      id: `tl-${at}-${this.profile.timeline.length}`,
      kind,
      title,
      location,
      at,
    };
    this.profile.timeline.push(event);
    if (this.profile.timeline.length > MAX_TIMELINE) {
      this.profile.timeline = this.profile.timeline.slice(-MAX_TIMELINE);
    }
  }

  /** Evaluate the achievement registry; unlock anything newly satisfied. */
  private evaluateAchievements(at: number): void {
    const unlockedIds = new Set(this.profile.achievements.map((a) => a.id));
    for (const rule of getAchievementRules()) {
      if (unlockedIds.has(rule.id)) continue;
      if (!rule.isSatisfied(this.profile)) continue;
      const achievement: PassportAchievement = {
        id: rule.id,
        title: rule.title,
        description: rule.description,
        unlockedAt: at,
        xpAwarded: rule.xpAwarded,
      };
      this.profile.achievements.push(achievement);
      this.awardXp(rule.xpAwarded);
      this.addTimeline('achievementEarned', `Earned ${rule.title}`, null, at);
      // Announce through Core — the module's only outbound channel.
      try {
        this.core?.publish('NotificationSent', MODULE_ID, {
          kind: 'achievementUnlocked',
          achievementId: rule.id,
          title: rule.title,
        }, at);
      } catch {
        /* announcement is best-effort */
      }
    }
  }

  private afterUpdate(at: number): void {
    this.evaluateAchievements(at);
    this.persist();
    for (const l of this.listeners) {
      try {
        l();
      } catch {
        /* one bad listener never breaks the rest */
      }
    }
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // ── Reads (deep-cloned) ─────────────────────────────────────────────────────

  getProfile(now: number = Date.now()): PassportProfile {
    return structuredClone({
      ...this.profile,
      favoriteDestination: this.favoriteCity(),
      longestStay: this.longestStay(now),
      totalDaysRoaming: this.totalDaysRoaming(now),
    });
  }

  getStamps(): PassportStamp[] {
    return structuredClone(this.profile.stamps);
  }

  getAchievements(): PassportAchievement[] {
    return structuredClone(this.profile.achievements);
  }

  getTimeline(): TimelineEvent[] {
    return structuredClone([...this.profile.timeline].sort((a, b) => a.at - b.at));
  }

  getMoments(): RoamerMoment[] {
    return structuredClone(this.profile.moments);
  }

  getStats(now: number = Date.now()): PassportStats {
    const streaks = this.streaks(now);
    return {
      countriesVisited: this.profile.countriesVisited.length,
      citiesVisited: this.profile.citiesVisited.length,
      currentStreakDays: streaks.current,
      longestStreakDays: streaks.longest,
      missionSuccessRate: null, // outcomes are not on the bus — never fabricated
      averageMonthlyBudgetUsd: null, // spend data is not on the bus — never fabricated
      totalDistanceKm: this.totalDistanceKm(), // null until coordinates flow on the bus
      favoriteCountry: this.favoriteCountry(),
      favoriteCity: this.favoriteCity(),
      favoriteCoworking: this.favoriteStampLocation('coworking'),
      favoriteCafe: this.favoriteStampLocation('coworking'),
      averageInternetMbps: null, // measurements are not on the bus — never fabricated
      daysAbroad: this.totalDaysRoaming(now),
      passportLevel: this.profile.currentLevel,
    };
  }

  // ── Derived statistics ──────────────────────────────────────────────────────

  private stayDays(stay: { arrivedAt: number; departedAt: number | null }, now: number): number {
    return Math.max(0, Math.floor(((stay.departedAt ?? now) - stay.arrivedAt) / DAY));
  }

  private totalDaysRoaming(now: number): number {
    return this.profile.stays.reduce((sum, s) => sum + this.stayDays(s, now), 0);
  }

  /** Haversine sum over consecutive stays with coordinates; null if <2 have them. */
  private totalDistanceKm(): number | null {
    const located = this.profile.stays.filter((s) => s.latitude != null && s.longitude != null);
    if (located.length < 2) return null;
    const R = 6371;
    const rad = (d: number) => (d * Math.PI) / 180;
    let total = 0;
    for (let i = 1; i < located.length; i++) {
      const a = located[i - 1];
      const b = located[i];
      const dLat = rad(b.latitude! - a.latitude!);
      const dLon = rad(b.longitude! - a.longitude!);
      const h = Math.sin(dLat / 2) ** 2 +
        Math.cos(rad(a.latitude!)) * Math.cos(rad(b.latitude!)) * Math.sin(dLon / 2) ** 2;
      total += 2 * R * Math.asin(Math.sqrt(h));
    }
    return Math.round(total);
  }

  private longestStay(now: number): { city: string; days: number } | null {
    let best: { city: string; days: number } | null = null;
    for (const s of this.profile.stays) {
      const days = this.stayDays(s, now);
      if (!best || days > best.days) best = { city: s.city, days };
    }
    return best;
  }

  private favoriteCity(): string | null {
    return this.mostDays((s) => s.city);
  }

  private favoriteCountry(): string | null {
    return this.mostDays((s) => s.country);
  }

  private mostDays(key: (s: PassportProfile['stays'][number]) => string | null): string | null {
    const totals = new Map<string, number>();
    const now = this.profile.stays.reduce((m, s) => Math.max(m, s.departedAt ?? s.arrivedAt), 0) || Date.now();
    for (const s of this.profile.stays) {
      const k = key(s);
      if (!k) continue;
      totals.set(k, (totals.get(k) ?? 0) + this.stayDays(s, now));
    }
    let best: string | null = null;
    let bestDays = -1;
    for (const [k, d] of totals) if (d > bestDays) { best = k; bestDays = d; }
    return best;
  }

  private favoriteStampLocation(category: StampCategory): string | null {
    const counts = new Map<string, number>();
    for (const s of this.profile.stamps) {
      if (s.category !== category) continue;
      counts.set(s.location, (counts.get(s.location) ?? 0) + 1);
    }
    let best: string | null = null;
    let bestCount = 0;
    for (const [loc, c] of counts) if (c > bestCount) { best = loc; bestCount = c; }
    return best;
  }

  private streaks(now: number): { current: number; longest: number } {
    const days = [...this.missionDays].sort((a, b) => a - b);
    if (days.length === 0) return { current: 0, longest: 0 };
    let longest = 1;
    let run = 1;
    for (let i = 1; i < days.length; i++) {
      run = days[i] === days[i - 1] + 1 ? run + 1 : 1;
      if (run > longest) longest = run;
    }
    const today = Math.floor(now / DAY);
    let current = 0;
    if (days[days.length - 1] === today || days[days.length - 1] === today - 1) {
      current = 1;
      for (let i = days.length - 1; i > 0; i--) {
        if (days[i] === days[i - 1] + 1) current += 1;
        else break;
      }
    }
    return { current, longest };
  }

  // ── Privacy, export, deletion ───────────────────────────────────────────────

  getPrivacy(): PassportPrivacy {
    return { ...this.privacy };
  }

  updatePrivacy(patch: Partial<PassportPrivacy>): PassportPrivacy {
    this.privacy = { ...this.privacy, ...patch };
    this.persist();
    return this.getPrivacy();
  }

  /** What others may see. Private passport ⇒ null. Each share flag gates its slice. */
  getPublicView(now: number = Date.now()): PublicPassportView | null {
    if (!this.privacy.publicPassport) return null;
    return structuredClone({
      passportId: this.profile.passportId,
      currentLevel: this.profile.currentLevel,
      timeline: this.privacy.shareTimeline ? this.getTimeline() : null,
      stats: this.privacy.shareStatistics ? this.getStats(now) : null,
      stamps: this.privacy.shareStamps ? this.profile.stamps : null,
    });
  }

  /** Full portable export (future printable passport / annual reports). */
  exportPassport(now: number = Date.now()): PassportExport {
    return structuredClone({
      exportedAt: now,
      profile: this.getProfile(now),
      privacy: this.privacy,
      stats: this.getStats(now),
    });
  }

  /** Delete Passport History — wipes everything, issues a fresh passport. */
  deleteHistory(now: number = Date.now()): void {
    this.profile = freshProfile(now);
    this.missionDays = [];
    this.persist();
    for (const l of this.listeners) {
      try {
        l();
      } catch { /* isolated */ }
    }
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState & { missionDays: number[] } = {
        profile: this.profile,
        privacy: this.privacy,
        missionDays: this.missionDays,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      storageManager.push('passport', 'state', state);
    } catch {
      /* storage unavailable — keep working in memory */
    }
  }

  /** Called by hydratePassportFromCloud when a newer cloud copy is available. */
  reloadFromStorage(now = Date.now()): void {
    this.load(now);
    for (const l of this.listeners) {
      try { l(); } catch { /* never break listeners */ }
    }
  }

  private load(now: number): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Record<string, unknown> | null;
      if (state == null || typeof state !== 'object') return;
      const p = state.profile as Record<string, unknown> | undefined;
      if (p == null || typeof p !== 'object' || typeof p.passportId !== 'string') return;
      const base = freshProfile(now);
      const strArr = (v: unknown): string[] =>
        Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
      this.profile = {
        ...base,
        passportId: p.passportId,
        userId: typeof p.userId === 'string' ? p.userId : base.userId,
        issuedAt: typeof p.issuedAt === 'number' ? p.issuedAt : now,
        totalXp: typeof p.totalXp === 'number' && p.totalXp >= 0 ? p.totalXp : 0,
        countriesVisited: strArr(p.countriesVisited),
        citiesVisited: strArr(p.citiesVisited),
        currentBase: p.currentBase != null && typeof p.currentBase === 'object' && typeof (p.currentBase as Record<string, unknown>).city === 'string'
          ? { city: (p.currentBase as Record<string, string>).city, country: typeof (p.currentBase as Record<string, unknown>).country === 'string' ? (p.currentBase as Record<string, string>).country : null }
          : null,
        missionCompletionCount: typeof p.missionCompletionCount === 'number' && p.missionCompletionCount >= 0 ? p.missionCompletionCount : 0,
        scoreHistory: (Array.isArray(p.scoreHistory) ? p.scoreHistory : [])
          .filter((s): s is { score: number; at: number } => s != null && typeof s === 'object' && typeof s.score === 'number' && typeof s.at === 'number')
          .slice(-MAX_SCORE_HISTORY),
        timeline: (Array.isArray(p.timeline) ? p.timeline : [])
          .filter((t): t is TimelineEvent => t != null && typeof t === 'object' && typeof t.id === 'string' && typeof t.title === 'string' && typeof t.at === 'number')
          .slice(-MAX_TIMELINE),
        stamps: (Array.isArray(p.stamps) ? p.stamps : [])
          .filter((s): s is PassportStamp =>
            s != null && typeof s === 'object' && typeof s.id === 'string' &&
            STAMP_CATEGORIES.includes(s.category) && typeof s.location === 'string' && typeof s.earnedAt === 'number'),
        achievements: (Array.isArray(p.achievements) ? p.achievements : [])
          .filter((a): a is PassportAchievement => a != null && typeof a === 'object' && typeof a.id === 'string' && typeof a.unlockedAt === 'number'),
        moments: (Array.isArray(p.moments) ? p.moments : [])
          .filter((m): m is RoamerMoment => m != null && typeof m === 'object' && typeof m.id === 'string' && typeof m.photoRef === 'string')
          .slice(-MAX_MOMENTS),
        stays: (Array.isArray(p.stays) ? p.stays : [])
          .filter((s): s is Record<string, unknown> => s != null && typeof s === 'object' && typeof s.city === 'string' && typeof s.arrivedAt === 'number')
          .map((s) => ({
            city: s.city as string,
            country: typeof s.country === 'string' ? s.country : null,
            arrivedAt: s.arrivedAt as number,
            departedAt: typeof s.departedAt === 'number' ? s.departedAt : null,
            latitude: typeof s.latitude === 'number' ? s.latitude : null,
            longitude: typeof s.longitude === 'number' ? s.longitude : null,
          })),
      };
      this.profile.currentLevel = levelForXp(this.profile.totalXp);
      this.missionDays = Array.isArray(state.missionDays)
        ? state.missionDays.filter((d): d is number => typeof d === 'number')
        : [];
      const priv = state.privacy as Record<string, unknown> | undefined;
      this.privacy = {
        publicPassport: priv?.publicPassport === true,
        shareTimeline: priv?.shareTimeline === true,
        shareStatistics: priv?.shareStatistics === true,
        shareStamps: priv?.shareStamps === true,
      };
    } catch {
      /* corrupt state — start with a fresh passport rather than crash */
    }
  }
}

/** App-wide singleton. Call attachToCore() during app bootstrap (after roamerCore.initialize()). */
export const roamerPassportModule = new RoamerPassportModule();

/**
 * Boot hydration: fetches the cloud copy of the Passport™ state if it is
 * strictly newer than the last local write, writes it to localStorage, and
 * signals the module to reload.  Fire-and-forget — boot never blocks the network.
 */
export async function hydratePassportFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<unknown>('passport', 'state');
  if (!cloud) return false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('nomad:passport:v1', JSON.stringify(cloud));
    }
  } catch { return false; }
  roamerPassportModule.reloadFromStorage();
  return true;
}
